import {
  AnotherPersonCartOverlay,
  DraftOverlay,
  SoldOverlay,
} from '@c/dashboard/pages/ListingsPage';
import { ChevronLeftIcon, ChevronRightIcon } from '@c/icons';
import { HeartIcon } from '@heroicons/react/20/solid';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import {
  addProductToFavorites,
  removeProductFromFavorites,
} from '@util/firestore/products';
import { trackUserInteraction } from '@util/firestore/recommendations';
import { useAuth } from 'context/AuthContext';
import { ProductDocument, Variation } from 'models/product';
import { UserDocument } from 'models/user';
import { MotionButton, MotionDiv } from 'motion';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import Hammer from 'react-hammerjs';
import SafeImage from './SafeImage';
import { cn } from '@util/index';
interface ProductCarouselProps {
  product: ProductDocument;
  openProductViewer: (index: number) => void;
  stack?: boolean;
  selectedVariations: Variation[] | null;
}

// function that wraps around the index to keep it within the range of 0 to count
const wrap = (index: number, count: number) => {
  if (index < 0) return count - 1;
  if (index >= count) return 0;
  return index;
};

const Dots = ({ count, active }: { count: number; active: number }) => {
  const dotsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dotsRef.current) return;

    const container = dotsRef.current;
    const activeDot = container.children[active] as HTMLElement;
    if (!activeDot) return;

    // Calculates the position of the dot relative to the container
    const dotLeft = activeDot.offsetLeft;
    const dotRight = dotLeft + activeDot.offsetWidth;
    const containerLeft = container.scrollLeft;
    const containerRight = containerLeft + container.offsetWidth;

    //If the dot is not visible, adjust the scroll
    if (dotLeft < containerLeft || dotRight > containerRight) {
      container.scrollTo({
        left: dotLeft - container.offsetWidth / 2 + activeDot.offsetWidth / 2,
        behavior: 'smooth',
      });
    }
  }, [active]);

  return (
    <div className="absolute bottom-0 left-0 right-0 mb-8 flex justify-center">
      <div
        ref={dotsRef}
        className="scrollbar-hide flex max-w-[20rem] gap-[1rem] overflow-x-auto overflow-y-hidden px-4 scrollbar-none"
      >
        {new Array(count).fill(1).map((_el, i) => (
          <MotionDiv
            key={`slider-${i}`}
            className="h-[.8rem] min-h-[.8rem] w-[.8rem] min-w-[.8rem] flex-shrink-0 rounded-full bg-brand-white"
            initial={false}
            animate={{
              scale: active === i ? 1.5 : 1,
              opacity: active === i ? 1 : 0.5,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export const handleLikeClick = async (
  e: React.MouseEvent,
  userDoc: UserDocument,
  id: string,
  setIsLiked: (isLiked: boolean) => void,
  isLiked: boolean,
  queryClient: QueryClient
) => {
  e.stopPropagation();

  if (!userDoc) return;
  const interaction = userDoc?.favorites?.includes(id)
    ? 'unfavorite'
    : 'favorite';
  if (interaction === 'unfavorite') {
    await removeProductFromFavorites(userDoc.uid, id);
  } else {
    await addProductToFavorites(userDoc.uid, id);
  }

  setIsLiked(!isLiked);

  queryClient.invalidateQueries({
    queryKey: ['authUser'],
  });
  return trackUserInteraction({
    uid: userDoc.uid,
    pid: id,
    interaction,
  });
};

const ImageSlider = ({
  images,
  title,
  video,
  id,
  favorite_count,
  is_auction,
  out_of_stock,
  seller_id,
}: Pick<
  ProductDocument,
  | 'images'
  | 'title'
  | 'video'
  | 'id'
  | 'favorite_count'
  | 'is_auction'
  | 'out_of_stock'
  | 'seller_id'
>) => {
  const constraintsRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  const { userDoc } = useAuth();
  const queryClient = useQueryClient();
  const router = useRouter();

  const totalSlides = images.length + (video ? 1 : 0);

  useEffect(() => {
    if (constraintsRef.current) {
      setContainerWidth(constraintsRef.current.offsetWidth);
    }
  }, []);

  const handleSwipe = (direction: number) => {
    if (direction === 4) {
      // Swipe right - go to previous
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
    }
    if (direction === 2) {
      // Swipe left - go to next
      setActiveIndex((prev) => (prev < totalSlides - 1 ? prev + 1 : prev));
    }
  };

  return (
    <div
      className={`relative aspect-square w-full overflow-hidden ${
        seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12'
          ? 'bg-white'
          : 'bg-brand-darker-white'
      }`}
      ref={constraintsRef}
    >
      {favorite_count >= 1 && !is_auction && !out_of_stock && (
        <div className="absolute left-2 right-0 top-0 z-20 mt-2 flex justify-end gap-[1rem] sm:hidden">
          <AnotherPersonCartOverlay count={favorite_count} />
        </div>
      )}

      {/* mobile like button */}
      <div className="absolute bottom-0 right-8 z-20 mb-8 flex justify-end gap-[1rem]">
        <MotionButton
          className={`flex h-[5rem] w-[5rem] items-center justify-center rounded-full p-4 ${
            !userDoc?.favorites.includes(id) ? 'bg-white' : 'bg-brand-secondary'
          }`}
          type="button"
          onClick={(e) => {
            if (userDoc) {
              handleLikeClick(e, userDoc, id, setIsLiked, isLiked, queryClient);
            } else {
              router.push('/login');
            }
          }}
          whileTap={{ scale: 0.8 }}
          animate={{
            scale: [1, 1.2, 1],
            transition: {
              duration: 0.3,
              times: [0, 0.5, 1],
              ease: 'easeInOut',
            },
          }}
          aria-label="Like product"
        >
          <HeartIcon
            className={`h-full w-full ${
              !userDoc?.favorites.includes(id)
                ? 'stroke-black text-white'
                : 'text-white'
            }`}
            aria-label="Like product"
          />
        </MotionButton>
      </div>

      {/* @ts-ignore */}
      <Hammer onSwipe={(e) => handleSwipe(e.direction)}>
        <div
          className="absolute inset-0 flex overflow-hidden transition-transform duration-300 ease-out"
          style={{
            transform: `translateX(-${activeIndex * containerWidth}px)`,
            width: `${totalSlides * containerWidth}px`,
          }}
        >
          {!!video && (
            <div
              className="relative h-full flex-shrink-0 bg-brand-primary-lighter"
              style={{ width: `${containerWidth}px` }}
            >
              <video
                loop
                muted
                playsInline
                className="h-full w-full object-cover"
                draggable={false}
                src={video.full}
              />
            </div>
          )}

          {images.map((image, i) => (
            <div
              key={`image-${i}-${image.thumb}`}
              className={`relative h-full flex-shrink-0 ${
                seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12'
                  ? 'bg-white'
                  : 'bg-brand-primary-lighter'
              }`}
              style={{ width: `${containerWidth}px` }}
            >
              <Image
                alt={title}
                src={image.thumb ?? image.download_url}
                className={`mx-auto h-full ${
                  image.thumb.includes('wpsstatic') ||
                  seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12'
                    ? 'object-contain'
                    : 'object-contain'
                }`}
                draggable={false}
                height={600}
                width={600}
                priority={i === 0}
                fetchPriority={i === 0 ? 'high' : 'low'}
              />
            </div>
          ))}
        </div>
      </Hammer>

      <Dots count={totalSlides} active={activeIndex} />
    </div>
  );
};

const ProductCarousel = ({
  product,
  openProductViewer,
  stack,
  selectedVariations,
}: ProductCarouselProps) => {
  const { userDoc } = useAuth();
  const [imageIndex, setImageIndex] = useState(product.video ? -1 : 0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const queryClient = useQueryClient();
  const router = useRouter();

  useEffect(() => {
    if (selectedVariations?.length) {
      const imageIndex = product.images.findIndex((image) => {
        const variation = selectedVariations
          .filter((v) => v.color)
          .find((v) => v.image === image.full || v.image === image.thumb);

        return variation;
      });

      const selectedColor = selectedVariations.find((v) => v.color);
      const colorVariantImage = product.variations?.find(
        (pv) =>
          pv.color === selectedColor?.color &&
          product.images.some(
            (img) => img.full === pv.image || img.thumb === pv.image
          )
      );
      if (colorVariantImage?.image) {
        const index = product.images.findIndex(
          (img) =>
            img.full === colorVariantImage.image ||
            img.thumb === colorVariantImage.image
        );

        setImageIndex(index);
      }

      if (imageIndex !== -1) {
        setImageIndex(imageIndex);
      }
    }
  }, [product.images, selectedVariations, product.variations]);

  const displayIndex = hoveredIndex !== null ? hoveredIndex : imageIndex;

  return (
    <>
      {/* mweb */}
      <div
        id="mobile-product-carousel"
        className="relative flex w-full sm:hidden"
        onClick={() => openProductViewer(imageIndex)}
      >
        <ImageSlider
          images={product.images}
          video={product.video}
          title={product.title}
          id={product.id}
          favorite_count={product.favorite_count}
          is_auction={product.is_auction}
          out_of_stock={product.out_of_stock}
          seller_id={product.seller_id}
        />

        {product.out_of_stock && !product.is_auction && (
          <SoldOverlay variant="sold" />
        )}
      </div>

      {/* dweb */}
      <div
        className={`hidden w-full max-w-full overflow-hidden ${
          stack ? 'gap-y-8 sm:flex sm:flex-col-reverse' : 'sm:flex'
        }`}
      >
        <div
          className="flex w-[80rem] items-center gap-x-4 gap-y-6 overflow-x-scroll"
          onMouseLeave={() => setHoveredIndex(null)}
        >
          {product.video && (
            <button
              className={cn(
                'group relative min-h-[10rem] min-w-[10rem] cursor-pointer rounded-[2rem] transition-opacity duration-150 ease-out hover:opacity-100',
                imageIndex === -1
                  ? 'border-2 border-brand-secondary opacity-100'
                  : 'border border-zinc-200 opacity-75 hover:border-zinc-400'
              )}
              onClick={() => setImageIndex(-1)}
              onMouseEnter={() => setHoveredIndex(-1)}
            >
              <video
                autoPlay
                loop
                muted
                playsInline
                className="h-[10rem] w-[10rem] rounded-[2rem] object-cover p-1 transition-all duration-100 ease-out group-hover:scale-95"
                draggable={false}
                src={product.video.full}
              />
            </button>
          )}

          {product.images.map((image, i) => {
            const isSelected = i === imageIndex;

            return (
              <button
                key={image.thumb}
                className={cn(
                  'group relative min-h-[10rem] min-w-[10rem] cursor-pointer rounded-[2rem] bg-[#F4F4F4] transition-opacity duration-150 ease-out hover:opacity-100',
                  isSelected
                    ? 'border-2 border-brand-secondary opacity-100'
                    : 'border border-zinc-200 opacity-75 hover:border-zinc-400'
                )}
                onClick={() => setImageIndex(i)}
                onMouseEnter={() => setHoveredIndex(i)}
              >
                <SafeImage
                  src={
                    image.thumb?.startsWith('https://cdn.wpsstatic.com/images/')
                      ? image.thumb.replace(/\/(500|1000)_max\//g, '/200_max/')
                      : image.thumb || image.download_url
                  }
                  fill
                  alt={product.title}
                  draggable={false}
                  className={cn(
                    'rounded-[2rem] p-1 transition-all duration-100 ease-out group-hover:scale-95',
                    image.thumb.includes('wpsstatic') ||
                      product.seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12'
                      ? 'object-contain'
                      : 'object-cover'
                  )}
                />
              </button>
            );
          })}
        </div>

        <div
          className={`flex h-fit w-full justify-center rounded-[2rem] ${
            product.seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12'
              ? 'bg-white'
              : 'bg-[#F2F2F2]'
          } sm:ml-4 sm:w-[80rem]`}
        >
          {/* desktop like button */}
          <div className="absolute right-8 top-8 z-20 mb-8 flex justify-end gap-[1rem]">
            <MotionButton
              className={`flex h-[5rem] w-[5rem] items-center justify-center rounded-full p-4 ${
                !userDoc?.favorites.includes(product.id)
                  ? 'bg-white'
                  : 'bg-brand-secondary'
              }`}
              type="button"
              onClick={(e) => {
                if (userDoc) {
                  handleLikeClick(
                    e,
                    userDoc,
                    product.id,
                    setIsLiked,
                    isLiked,
                    queryClient
                  );
                } else {
                  router.push('/login');
                }
              }}
              whileTap={{ scale: 0.8 }}
              animate={{
                scale: [1, 1.2, 1],
                transition: {
                  duration: 0.3,
                  times: [0, 0.5, 1],
                  ease: 'easeInOut',
                },
              }}
              aria-label="Like product"
            >
              <HeartIcon
                className={`h-full w-full ${
                  !userDoc?.favorites.includes(product.id)
                    ? 'stroke-black text-white'
                    : 'text-white'
                }`}
                aria-label="Like product"
              />
            </MotionButton>
          </div>

          <div
            onClick={() => openProductViewer(imageIndex)}
            className="relative aspect-square w-full cursor-zoom-in sm:w-[57rem]"
          >
            {displayIndex === -1 && product.video ? (
              <video
                autoPlay
                loop
                muted
                controls={true}
                playsInline
                draggable={false}
                src={product.video.full}
                className="h-full w-full rounded-[1.5rem] bg-[#F4F4F4] object-contain"
              />
            ) : (
              <SafeImage
                src={product.images[displayIndex]?.thumb}
                alt={product.title}
                fill
                width={1000}
                height={1000}
                className={`${
                  product.images[displayIndex]?.thumb.includes('wpsstatic') ||
                  product.seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12'
                    ? 'object-contain'
                    : 'object-cover'
                }`}
                key={`image-${product.images[displayIndex]?.thumb}`}
                fallbackSrc={product.images[displayIndex]?.download_url}
                priority={imageIndex === 0}
                fetchPriority={imageIndex === 0 ? 'high' : 'low'}
              />
            )}

            {product.is_draft &&
            (product.seller_id === userDoc?.uid || !!userDoc?.roles?.admin) ? (
              <DraftOverlay variant="draft" />
            ) : product.start_time &&
              product.start_time > Date.now() &&
              product.date_added ? (
              <DraftOverlay variant="auction" />
            ) : product.is_auction &&
              product.end_time &&
              product.end_time < Date.now() ? (
              <SoldOverlay variant="auction" />
            ) : product.out_of_stock &&
              (!product.is_auction || !product.date_added) ? (
              <SoldOverlay variant="sold" />
            ) : null}

            {product.favorite_count >= 1 &&
              !product.is_auction &&
              !product.out_of_stock && (
                <AnotherPersonCartOverlay count={product.favorite_count} />
              )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductCarousel;
